<template>
    <div class="reference-content">
        <div v-if="loading" class="reference-state" role="status">
            <span class="spinner-border spinner-border-sm"></span>
            {{ $t('patty.auditReferences.states.loading') }}
        </div>
        <div v-else-if="error" class="reference-state error" role="alert">
            <i class="fi fi-alert-circle"></i>
            {{ error }}
        </div>
        <template v-else-if="record">
            <PattyAuditRecordReadOnly
                v-if="reference.type === 'AUDIT_RECORD'"
                :audit="record"
            />
            <ViewAuditGap
                v-else-if="reference.type === 'AUDIT_GAP'"
                :gap="record"
                :linked-action-points="linkedActionPoints"
                @open-action-point="openActionPoint"
            />
            <ViewActionPoint
                v-else-if="reference.type === 'ACTION_POINT'"
                :action-point="record"
            />
        </template>
        <div v-else class="reference-state">
            {{ $t('patty.auditReferences.states.unavailable') }}
        </div>
    </div>
</template>

<script>
import ViewAuditGap from '@/components/audits/ViewAuditGap.vue';
import ViewActionPoint from '@/components/action-items/ViewActionPoint.vue';
import PattyAuditRecordReadOnly from './PattyAuditRecordReadOnly.vue';
import {
    createAuditAgentApi,
    normalizeReferenceResponse,
} from './auditAgent.js';

export default {
    name: 'PattyReferenceSidekick',
    components: { PattyAuditRecordReadOnly, ViewAuditGap, ViewActionPoint },
    props: {
        reference: { type: Object, required: true },
    },
    emits: ['open-reference'],
    data() {
        return {
            loading: false,
            error: '',
            record: null,
            linkedActionPoints: [],
        };
    },
    watch: {
        reference: {
            immediate: true,
            handler() {
                this.loadReference();
            },
        },
    },
    methods: {
        clearReference() {
            this.loading = false;
            this.error = '';
            this.record = null;
            this.linkedActionPoints = [];
        },
        openActionPoint(actionPoint) {
            if (!actionPoint?.hash) return;
            this.$emit('open-reference', {
                type: 'ACTION_POINT',
                hash: actionPoint.hash,
                label:
                    actionPoint.action_point ||
                    actionPoint.actionPoint ||
                    this.$t('patty.auditReferences.types.actionPoint'),
            });
        },
        async loadReference() {
            this.clearReference();
            const requestedReference = this.reference;
            this.loading = true;
            try {
                const response = await createAuditAgentApi({
                    apiRoot: this.$env.apiRoot,
                }).getReference(requestedReference);
                if (this.reference !== requestedReference) return;
                const normalized = normalizeReferenceResponse(
                    requestedReference.type,
                    response
                );
                this.record = normalized.record;
                this.linkedActionPoints = normalized.linkedActionPoints;
            } catch (error) {
                if (this.reference !== requestedReference) return;
                this.error =
                    error?.status === 404
                        ? this.$t('patty.auditReferences.states.unavailable')
                        : error?.message ||
                          this.$t('patty.auditReferences.states.loadError');
            } finally {
                if (this.reference === requestedReference) this.loading = false;
            }
        },
    },
};
</script>

<style scoped>
.reference-content {
    min-height: 100%;
}

.reference-state {
    min-height: 10rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    color: var(--neutral-700);
    text-align: center;
}

.reference-state.error {
    color: var(--danger);
}
</style>
